import { useCallback, useEffect, useMemo } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import uniq from 'lodash-es/uniq';
import { AlertTriangleIcon, CircleHelpIcon } from 'lucide-react';
import { Controller, FieldError, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';

import { Chips } from '@/components/atoms/Chip/Chip';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { ReadOnlyField } from '@/components/atoms/ReadOnlyField/ReadOnlyField';
import { ToolkitEditor } from '@/components/organisms/Toolkit/ToolkitEditor/ToolkitEditor';
import { CharacterCounter } from '@/components/organisms/Toolkit/ToolkitEditorTabs/CharacterCounter';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AutocompleteOption, Autocomplete } from '@/components/ui/custom/Autocomplete/Autocomplete';
import { Input } from '@/components/ui/input';
import useDialogAlert from '@/hooks/useDialogAlert';
import useDialogs from '@/hooks/useDialogs';
import i18n from '@/i18n/i18n';
import { getVariableLabel, REVERSE_VARIABLE_LABELS } from '@/redux/dialogs/helper';
import { selectNodeIdByActionId, selectSelectedAction } from '@/redux/dialogs/selectors';
import { updateAction } from '@/redux/nodes/actions';
import { selectBrainId } from '@/redux/session/selectors';
import { Action } from '@/shared/types/action';
import { RootState } from '@/shared/types/state';
import { MAX_EMAIL_RECIPIENTS } from '@/util/constants';
import {
  capitalizeFirstLetter,
  formatErrorMessage,
  wrapDollarSignWithCurlyBraces,
} from '@/util/util';
import { LENGTH_1024, LENGTH_S } from '@/util/validation/constants';
import { emailPattern, entityOrContext } from '@/util/validation/patterns';

import ToolkitWrapper from '../../ToolkitWrapper';

/** Standalone schema for validating a single recipient entry */
const recipientSchema = yup
  .string()
  .test({
    message: () => i18n.t('dialog.errors.email_recipient'),
    test: (value) =>
      value === '' || value === null || entityOrContext.test(value) || emailPattern.test(value),
  })
  .nullable()
  .defined(() => i18n.t('dialog.errors.email_recipient'));

const emailToolkitSchema = yup.object().shape({
  subject: yup
    .string()
    .max(LENGTH_S)
    .required(() => i18n.t('validation.required')),
  body: yup
    .string()
    .min(1, () => i18n.t('validation.required_field', { 0: i18n.t('dialog.email.body') }))
    .max(LENGTH_1024 * 2, () => i18n.t('rich_text_editor.errors.char_limit')),
  to: yup
    .array()
    .min(1, () => i18n.t('dialog.errors.at_least_one', { 0: i18n.t('dialog.email.recipient') }))
    .max(MAX_EMAIL_RECIPIENTS),
  recipient: recipientSchema,
});

type ActionEmailForm = yup.InferType<typeof emailToolkitSchema>;

const ToolkitEmail = () => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const { type, actionId, to, body, subject, from } = useSelector((state: RootState) => {
    const selectedAction = selectSelectedAction(state) as Extract<Action, { type: 'email' }>;
    const { action_id, type, to, body, subject, from } = selectedAction;
    return {
      type,
      actionId: action_id,
      to: to || [],
      body: body || '',
      subject: subject || '',
      from: from,
    };
  }, shallowEqual);
  const parentNodeId = useSelector(selectNodeIdByActionId(actionId));

  const brainId = useSelector(selectBrainId);
  const { getContextVariables } = useDialogs(brainId);
  const contextVariables = useMemo(() => getContextVariables(), [getContextVariables]);

  // Transform options to FormAutocomplete format (type -> category)
  const comboboxOptions = useMemo<AutocompleteOption[]>(
    () =>
      contextVariables.map((opt) => ({
        value: opt.value,
        label: opt.label,
        category: t(`auto_complete.group_labels.${opt.type}`),
      })),
    [contextVariables, t]
  );

  const defaultTo = useMemo(() => to.map((item) => item?.replace(/{{|}}/g, '')), [to]);

  const {
    formState: { errors },
    setValue,
    trigger,
    control,
  } = useForm<ActionEmailForm>({
    mode: 'onChange',
    defaultValues: { recipient: null, to: defaultTo, subject, body },
    resolver: yupResolver(emailToolkitSchema) as never,
  });

  // Re-sync form when action changes (e.g., switching between actions or after async load)
  useEffect(() => {
    setValue('body', body);
    setValue('subject', subject);
    setValue('to', defaultTo);
  }, [actionId, body, subject, defaultTo, setValue]);

  // Dialog error messages
  const subjectErrorMessage = formatErrorMessage(errors.subject);
  const toErrorMessage = formatErrorMessage(errors.to as FieldError);
  const bodyErrorMessage = formatErrorMessage(errors.body);

  // Watch subject for character counter
  const subjectValue = useWatch({ control, name: 'subject' });

  const handleOnRecipientDelete = useCallback(
    (rec) => {
      const newRecipients = to.filter(
        (recipient) => recipient !== wrapDollarSignWithCurlyBraces(rec)
      );

      dispatch(updateAction({ actionId, action: { to: newRecipients } }));
    },
    [actionId, dispatch, to]
  );

  const { setFieldError } = useDialogAlert({
    id: actionId,
    nodeId: parentNodeId,
    title: t('actions.types.email'),
    type,
  });

  const handleChange = useCallback(
    (key: string, value: string | string[]) => {
      // Prevent dispatching the same value
      if (key === 'body' && body === value) {
        return;
      }

      const updatedAction = { type, to, body, from, subject, [key]: value };

      dispatch(updateAction({ actionId, action: updatedAction }));
    },
    [actionId, body, dispatch, from, subject, to, type]
  );

  // Single handler for both onChange (existing option pick) and onCreateNew
  // (unlisted entry). Validating in one place closes the prior dual-fire bug
  // where invalid values reached Redux via the onChange path before
  // onCreateNew could reject them.
  const handleAddRecipient = useCallback(
    (newValue: string) => {
      const trimmed = newValue?.trim();
      if (!trimmed || !recipientSchema.isValidSync(trimmed)) {
        return;
      }

      const newRecipients = uniq([...to, wrapDollarSignWithCurlyBraces(trimmed)]) as string[];
      setValue('to', uniq([...defaultTo, trimmed]), { shouldValidate: true });
      handleChange('to', newRecipients);
    },
    [defaultTo, handleChange, setValue, to]
  );

  useEffect(() => {
    trigger();
  }, [trigger]);

  useEffect(() => {
    setFieldError('subject', subjectErrorMessage);
  }, [subjectErrorMessage, setFieldError]);

  useEffect(() => {
    setFieldError('body', bodyErrorMessage);
  }, [bodyErrorMessage, setFieldError]);

  useEffect(() => {
    setFieldError('to', toErrorMessage);
  }, [toErrorMessage, setFieldError]);

  return (
    <ToolkitWrapper type={type}>
      <ReadOnlyField
        className="mb-6"
        label={t('dialog.email.from')}
        value={t('dialog.email.from_address')}
        tooltip={t('dialog.email.from_tooltip')}
      />

      <div className="mb-6">
        <Autocomplete
          label={
            <div className="mb-1 flex items-center gap-1">
              <label className="text-xs font-medium text-foreground-muted">
                {capitalizeFirstLetter(t('dialog.email.recipient'))}
              </label>
              <InfoTooltip content={t('dialog.email.to_tooltip')} side="right">
                <CircleHelpIcon size={12} className="text-foreground-muted" />
              </InfoTooltip>
            </div>
          }
          options={comboboxOptions}
          value=""
          onChange={handleAddRecipient}
          placeholder={t('dialog.email.to_placeholder')}
          disabled={to?.length === MAX_EMAIL_RECIPIENTS}
          onCreateNew={handleAddRecipient}
          createNewDisabled={(search) => !recipientSchema.isValidSync(search.trim())}
        />

        <Controller
          name="to"
          control={control}
          render={({ field }) => {
            return (
              <Chips
                className="mt-2 max-h-[200px]"
                chips={field.value.map((item) => getVariableLabel(item))}
                onDelete={(recipient) => {
                  const rec = REVERSE_VARIABLE_LABELS[recipient] || recipient;

                  handleOnRecipientDelete(rec);
                  field.onChange(field.value.filter((r) => r !== rec));
                }}
              />
            );
          }}
        />
      </div>

      {to?.length === 0 && (
        <div className="mb-6">
          <Alert variant="destructive">
            <AlertTriangleIcon size={16} />
            <AlertDescription>
              {t('dialog.errors.at_least_one', { 0: t('dialog.email.recipient') })}
            </AlertDescription>
          </Alert>
        </div>
      )}

      <div className="mb-6">
        <CharacterCounter
          label={t('dialog.email.subject')}
          currentLength={subjectValue?.length || 0}
          maxLength={LENGTH_S}
          error={!!errors.subject}
        />
        <Controller
          control={control}
          name="subject"
          render={({ field }) => (
            <Input
              id="subject"
              placeholder={t('dialog.email_subject')}
              aria-invalid={!!errors.subject}
              {...field}
              value={field.value ?? ''}
              onChange={(event) => {
                field.onChange(event);
                handleChange('subject', event.target.value);
              }}
            />
          )}
        />
        {subjectErrorMessage && <p className="mt-1 text-sm text-warning">{subjectErrorMessage}</p>}
      </div>

      <Controller
        name="body"
        control={control}
        render={({ field }) => (
          <div>
            <label className="mb-2 block text-xs font-medium text-foreground-muted uppercase">
              {t('dialog.email.body')}
            </label>
            <ToolkitEditor
              content={field.value}
              onContentChange={(content) => {
                field.onChange(content);
                handleChange('body', content);
              }}
              variableOptions={contextVariables}
              maxLength={2 * LENGTH_1024}
              placeholder={t('dialog.email.body_placeholder')}
            />
            {bodyErrorMessage && <p className="mt-1 text-sm text-red-600">{bodyErrorMessage}</p>}
          </div>
        )}
      />
    </ToolkitWrapper>
  );
};

export default ToolkitEmail;
